/**
 * WhatsApp Flow Responses TanStack Query Hooks
 *
 * React Query hooks for the Flow Responses closer dashboard.
 * Ingestion happens on the API webhook side — these hooks just read and mutate.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  waApi,
  type WAFlowResponsesListParams,
  type WAFlowResponsesListResponse,
  type WAFlowResponseStats,
  type WAFlowResponseDetail,
  type WAFlowResponseRow,
  type WAFlowResponseStatusInput,
  type WAFlowCloser,
} from '@/lib/api-client'

export const waFlowResponsesKeys = {
  all: ['wa-flow-responses'] as const,
  lists: () => [...waFlowResponsesKeys.all, 'list'] as const,
  list: (params?: WAFlowResponsesListParams) => [...waFlowResponsesKeys.lists(), params] as const,
  stats: () => [...waFlowResponsesKeys.all, 'stats'] as const,
  details: () => [...waFlowResponsesKeys.all, 'detail'] as const,
  detail: (id: number) => [...waFlowResponsesKeys.details(), id] as const,
  closers: () => [...waFlowResponsesKeys.all, 'closers'] as const,
}

/**
 * List flow responses with filter + pagination
 */
export function useWAFlowResponses(params?: WAFlowResponsesListParams) {
  return useQuery<WAFlowResponsesListResponse>({
    queryKey: waFlowResponsesKeys.list(params),
    queryFn: () => waApi.flowResponses.list(params),
    staleTime: 30 * 1000,
  })
}

/**
 * Stats cards: HOT/WARM/COLD counts and status breakdown
 */
export function useWAFlowResponseStats() {
  return useQuery<WAFlowResponseStats>({
    queryKey: waFlowResponsesKeys.stats(),
    queryFn: () => waApi.flowResponses.stats(),
    staleTime: 30 * 1000,
  })
}

/**
 * Detail view — includes the full parsed response JSON and joined contact/patient
 */
export function useWAFlowResponse(id: number | null) {
  return useQuery<WAFlowResponseDetail>({
    queryKey: waFlowResponsesKeys.detail(id ?? 0),
    queryFn: () => waApi.flowResponses.get(id!),
    enabled: !!id,
  })
}

/**
 * List of staff available as closers (for assignment dropdown)
 */
export function useWAFlowClosers() {
  return useQuery<{ data: WAFlowCloser[] }>({
    queryKey: waFlowResponsesKeys.closers(),
    queryFn: () => waApi.flowResponses.closers(),
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Assign a flow response to a closer (or clear assignment with null)
 */
export function useAssignWAFlowResponse() {
  const qc = useQueryClient()
  return useMutation<WAFlowResponseRow, Error, { id: number; assignedUserId: number | null }>({
    mutationFn: ({ id, assignedUserId }) => waApi.flowResponses.assign(id, assignedUserId),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: waFlowResponsesKeys.detail(id) })
      qc.invalidateQueries({ queryKey: waFlowResponsesKeys.lists() })
      qc.invalidateQueries({ queryKey: waFlowResponsesKeys.stats() })
    },
  })
}

/**
 * Update status — contacted/booked/attended/converted/lost
 */
export function useUpdateWAFlowResponseStatus() {
  const qc = useQueryClient()
  return useMutation<WAFlowResponseRow, Error, { id: number; data: WAFlowResponseStatusInput }>({
    mutationFn: ({ id, data }) => waApi.flowResponses.updateStatus(id, data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: waFlowResponsesKeys.detail(id) })
      qc.invalidateQueries({ queryKey: waFlowResponsesKeys.lists() })
      qc.invalidateQueries({ queryKey: waFlowResponsesKeys.stats() })
    },
  })
}

/**
 * Update closer notes
 */
export function useUpdateWAFlowResponseNotes() {
  const qc = useQueryClient()
  return useMutation<WAFlowResponseRow, Error, { id: number; notes: string }>({
    mutationFn: ({ id, notes }) => waApi.flowResponses.updateNotes(id, notes),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: waFlowResponsesKeys.detail(id) })
      qc.invalidateQueries({ queryKey: waFlowResponsesKeys.lists() })
    },
  })
}

export function getWAFlowResponseExportUrl(): string {
  return waApi.flowResponses.getExportUrl()
}
